import React, { useState, useRef, useEffect } from 'react';
import {
  // functions for API calls
  fetchEmployeeDetails,
  fetchUserProfilePicture,
  uploadUserProfilePicture,
  deleteUserProfilePicture,
  changeUserPassword,
} from 'utils/api';
import { useLoader } from 'contexts/LoaderContext';
import { useCustomToast } from 'utils/toastUtils';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { LOCAL_STORAGE_KEYS, formatDate } from 'utils/constants';
import {
  // Chakra UI components for layout and design
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  Avatar,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  HStack,
  useToast,
  ModalCloseButton,
  Grid,
  GridItem,
  FormLabel,
} from '@chakra-ui/react';
import {
  // Icon imports
  FaEnvelope,
  FaTransgender,
  FaMapMarkerAlt,
  FaNetworkWired,
  FaBirthdayCake,
  FaHeart,
  FaCalendar,
  FaUserTie,
  FaUsers,
  FaTrash,
  FaIdBadge,
  FaCamera,
  FaHeartbeat,
  FaBarcode,
  FaKey,
  FaUserTag,
  FaUserCheck,
  FaAmbulance,
} from 'react-icons/fa';
import {
  MdPhone,
  MdPhotoCamera,
  MdVisibility,
  MdVisibilityOff,
  MdContactPhone,
} from 'react-icons/md';

export default function UserDetailsPage() {
  // ==================== STATE VARIABLES ====================
  const emp_code = localStorage.getItem('emp_code'); // Fetch employee code from localStorage
  const showToast = useCustomToast(); // Custom hook to show styled toasts
  const [activeTab, setActiveTab] = useState(0); // State to manage currently active tab
  const [userDetails, setUserDetails] = useState(null); // Stores all user detail fields (personal + professional)
  const toast = useToast(); // Chakra UI toast instance
  const [profilePhoto, setProfilePhoto] = useState(null); // Holds profile picture
  const [isProfilePhotoModalOpen, setProfilePhotoModalOpen] = useState(false); // State to handle profile photo modal visibility
  const [initialUserDetails, setInitialUserDetails] = useState(null); // Stores initial user data
  const fileInputRef = useRef(null); // Reference to hidden file input element
  const { showGlobalLoader, hideGlobalLoader } = useLoader();
  const [isPhotoSubmitting, setIsPhotoSubmitting] = useState(false);

  // ==================== CHANGE PASSWORD MODAL STATE ====================
  const [isChangePasswordModalOpen, setChangePasswordModalOpen] =
    useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Change Password Modal control
  const handleOpenChangePasswordModal = () => setChangePasswordModalOpen(true);
  const handleCloseChangePasswordModal = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordErrors({
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setChangePasswordModalOpen(false); // <-- Corrected here
  };

  // Password validation function
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return '';
  };

  const handleChangePassword = async () => {
    // Reset errors
    const errors = {
      oldPassword: '',
      newPassword: '',
      confirmPassword: '',
    };

    // Required field validations
    if (!oldPassword.trim()) {
      errors.oldPassword = 'Current password is required';
    }

    if (!newPassword.trim()) {
      errors.newPassword = 'New password is required';
    } else {
      // Validate new password strength
      const passwordError = validatePassword(newPassword);
      if (passwordError) {
        errors.newPassword = passwordError;
      }
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = 'Please confirm your new password';
    }

    // Check if passwords match
    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    // Check if new password is same as old password
    if (oldPassword && newPassword && oldPassword === newPassword) {
      errors.newPassword =
        'New password must be different from current password';
    }

    // Set errors if any
    if (errors.oldPassword || errors.newPassword || errors.confirmPassword) {
      setPasswordErrors(errors);
      return;
    }

    // Proceed with password change
    setChangingPassword(true);
    try {
      // 🔹 Call actual backend API
      const response = await changeUserPassword(
        emp_code, // employee code from localStorage
        oldPassword, // current password entered by user
        newPassword, // new password entered by user
      );

      // 🔹 If backend returns success
      showToast('success', response.message || 'Password changed successfully');
      handleCloseChangePasswordModal();
    } catch (error) {
      // 🔹 Handle backend or network errors
      showToast('error', error.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // ==================== MODAL CONTROL FUNCTIONS ====================
  const handleOpenProfilePhotoModal = () => setProfilePhotoModalOpen(true); // Opens the profile picture management modal
  const handleCloseProfilePhotoModal = () => setProfilePhotoModalOpen(false); // Closes the profile picture management modal

  // ==================== EFFECT HOOKS ====================

  /**
 Effect: Fetch and load user details on component mount 
 Retrieves employee data from API and formats it for display.
 Handles error states and shows appropriate toast notifications.
 */
  useEffect(() => {
    //function to load user details from API
    const loadUserDetails = async () => {
      const roleId = localStorage.getItem(LOCAL_STORAGE_KEYS.ROLE_ID);
      if (!emp_code || !roleId) return; // Early return if required data is missing

      showGlobalLoader();
      try {
        // Fetch employee data from API
        const response = await fetchEmployeeDetails(emp_code, roleId);
        const { employeeData, roletitle } = response;
        // Format API response data for component consumption
        const formattedData = {
          name: employeeData.username,
          emailId: employeeData.email_id,
          phoneNo: employeeData.phoneNo,
          location: employeeData.location,
          dateOfBirth: employeeData.dateofbirth,
          gender: employeeData.gender,
          maritalStatus: employeeData.maritalStatus,
          emgContactName: employeeData.emg_ContactName,
          emgContactPhoneNo: employeeData.emg_ContactPhoneNo,
          emgContactRelation: employeeData.emg_ContactRelation,
          designation: employeeData.designation,
          isBillable: employeeData.isBillable,
          employmentType: employeeData.employmentType,
          employmentType_Id: employeeData.employmentType_Id,
          departmentName: employeeData.departmentName,
          teamName: employeeData.teamName,
          reportingTo: employeeData.reportingTo,
          joiningDate: employeeData.joiningDate,
          emp_code: employeeData.emp_code,
          roleTitle: roletitle,
        };

        setUserDetails(formattedData); // Update state with formatted data
        setInitialUserDetails(formattedData);
      } catch (error) {
        console.error('Error fetching Employee details:', error);

        showToast(
          'error',
          'Unable to load Employee details. Please try again later.',
        );
      } finally {
        hideGlobalLoader();
      }
    };

    loadUserDetails();
  }, [emp_code, toast]);

  // Effect: Fetch user profile picture after user details are loaded
  useEffect(() => {
    const loadProfilePicture = async () => {
      if (!userDetails || !userDetails.emp_code) return; // Early return if user details aren't loaded yet

      const imageUrl = await fetchUserProfilePicture(userDetails.emp_code);
      if (imageUrl) setProfilePhoto(imageUrl);
    };

    loadProfilePicture();
  }, [userDetails]);

  /**
   Effect: Disable page scrolling while component is mounted
  Prevents background scrolling for better UX when viewing profile. 
  Cleanup function restores normal scrolling on component unmount.
 */
  useEffect(() => {
    // Disable scroll on the body
    document.body.style.overflow = 'hidden';

    // Clean up: Restore the scroll when the component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // ==================== EVENT HANDLERS ====================

  //Handles profile picture upload process
  const handleProfileUpload = async (event) => {
    if (!userDetails || !userDetails.emp_code) {
      // Validate user details are loaded
      console.error('User details not loaded yet.');
      return;
    }

    const file = event.target.files[0];
    if (!file) return;

    showGlobalLoader();
    try {
      await uploadUserProfilePicture(file, userDetails.emp_code); // Upload image to server
      const imageUrl = URL.createObjectURL(file); // Create local preview URL for immediate UI update
      setProfilePhoto(imageUrl);
      showToast('success', 'Profile picture updated successfully!'); // Show success notification and reload page after delay
      setTimeout(() => {
        window.location.reload();
      }, 2500);
    } catch (error) {
      showToast('error', "Couldn't upload profile picture. Try again.");
      hideGlobalLoader();
    }
  };

  // Handles profile picture deletion process
  const handleDeleteProfilePhoto = async () => {
    if (!userDetails || !userDetails.emp_code) {
      // Validate user details are available
      showToast('error', "Couldn't upload profile picture. Try again.");
      return;
    }

    try {
      setIsPhotoSubmitting(true);
      await deleteUserProfilePicture(userDetails.emp_code); // Delete profile picture from server
      setProfilePhoto(null); // Clear local profile photo state

      showToast('success', 'profile photo has been deleted successfully!'); // Show success notification and reload page after delay
      setTimeout(() => {
        window.location.reload();
      }, 2500);
    } catch (error) {
      showToast('warning', "Can't remove the default profile picture.");
    } finally {
      setIsPhotoSubmitting(false);
      handleCloseProfilePhotoModal(); // Always close modal regardless of success/failure
    }
  };

  //Programmatically triggers the hidden file input click
  const openFileInput = () => {
    fileInputRef.current.click();
  };
  // ==================== UTILITY COMPONENTS ====================
  const InfoRow = ({ icon: IconComponent, label, value, children }) => (
    <Flex
      align="center"
      py={3}
      borderBottom="1px solid"
      borderColor="gray.200"
      textAlign="left"
      minH="80px"
    >
      <Icon as={IconComponent} color="gray.500" mr={4} boxSize={5} />
      <Flex width="full" flexDirection="column" align="flex-start">
        <Text fontWeight="medium" color="gray.600" mb={1}>
          {label}
        </Text>
        {value ? (
          <Text fontWeight="semibold" color="gray.800">
            {value}
          </Text>
        ) : (
          children || <Text color="gray.400">NA</Text>
        )}
      </Flex>
    </Flex>
  );
  // ==================== MAIN COMPONENT RENDER ====================
  return (
    <Flex
      width="100%"
      height="94vh"
      bg="white"
      align="center"
      justify="center"
      p={6}
      overflow="hidden"
    >
      <Box
        width="100%"
        minH="90vh"
        maxWidth="1000px"
        bg="white"
        border="1px solid"
        borderColor="gray.100"
        borderRadius="lg"
        padding='30px 1px 0px 1px'

      >
        <ToastContainer /> {/* Toast notification container */}
        {/* ==================== HEADER SECTION ==================== */}
        <Flex
          bg="gray.100"
          color="gray.900"
          p={6}
          align="center"
          borderBottom="1px solid"
          borderColor="gray.100"
        >
          {/* Profile picture with camera overlay */}
          <Box position="relative" width="fit-content">
            <Avatar
              size="xl"
              name={userDetails?.name}
              src={profilePhoto || undefined}
              bg="gray.200"
              color="gray.800"
              mr={6}
              borderWidth={2}
              borderColor="gray.600"
              cursor="pointer"
            />
            {/* Camera icon overlay for profile picture editing */}
            {/* <Icon
              as={MdPhotoCamera}
              position="absolute"
              bottom={-2}
              right={4}
              borderRadius="full"
              bg="gray.100"
              color="gray.700"
              p={2}
              boxSize={8}
              cursor="pointer"
              onClick={handleOpenProfilePhotoModal}
              boxShadow="md"
              _hover={{
                bg: 'gray.200',
                color: 'gray.900',
              }}
              transition="all 0.2s"
            /> */}
            {/* Hidden file input for profile picture upload */}
            <Input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/jpeg,image/png,image/gif"
              onChange={handleProfileUpload}
            />
          </Box>
          {/* User name and job title display */}
          <VStack align="flex-start" spacing={1} flex={1}>
            <Heading size="lg" color="gray.800">
              {userDetails?.name || 'Loading...'}
            </Heading>

            <Text color="gray.600">{userDetails?.jobTitle}</Text>
          </VStack>
        </Flex>
        {/* ==================== TABBED CONTENT SECTION ==================== */}
        <Tabs
          variant="line"
          colorScheme="gray"
          px={6}
          onChange={(index) => setActiveTab(index)}
        >
          {/* Tab navigation */}
          <TabList>
            <Tab
              _selected={{
                borderBottomColor: 'gray.600',
                color: 'gray.800',
                fontWeight: 'semibold',
              }}
            >
              Professional Details
            </Tab>
            <Tab
              _selected={{
                borderBottomColor: 'gray.600',
                color: 'gray.800',
                fontWeight: 'semibold',
              }}
            >
              Personal Details
            </Tab>
          </TabList>
          <TabPanels>
            {/* ==================== PROFESSIONAL DETAILS TAB ==================== */}
            <TabPanel>
              <Grid
                templateColumns="repeat(2, 1fr)"
                gap={4}
                alignItems="stretch"
              >
                <GridItem>
                  <InfoRow
                    icon={FaBarcode}
                    label="Employee Code"
                    value={`E0${userDetails?.emp_code}`}
                  />
                </GridItem>

                <GridItem>
                  <InfoRow
                    icon={FaIdBadge}
                    label="Designation"
                    value={userDetails?.designation}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaUserCheck}
                    label="Billable"
                    value={userDetails?.isBillable ? 'Yes' : 'No'}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaUserTag}
                    label="Employment Type"
                    value={userDetails?.employmentType}
                  />
                </GridItem>

                <GridItem>
                  <InfoRow
                    icon={FaNetworkWired}
                    label="Department"
                    value={userDetails?.departmentName}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaUsers}
                    label="Team"
                    value={userDetails?.teamName}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaUserTie}
                    label="Reporting To"
                    value={userDetails?.reportingTo}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaCalendar}
                    label="Joining Date"
                    value={formatDate(userDetails?.joiningDate)}
                  />
                </GridItem>
              </Grid>
            </TabPanel>
            {/* ==================== PERSONAL DETAILS TAB ==================== */}
            <TabPanel>
              <Grid
                templateColumns="repeat(2, 1fr)"
                gap={4}
                // mt={4}
                alignItems="stretch"
              >
                <GridItem>
                  <InfoRow
                    icon={FaEnvelope}
                    label="Email"
                    value={userDetails?.emailId}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={MdPhone}
                    label="Phone"
                    value={userDetails?.phoneNo}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaMapMarkerAlt}
                    label="Location"
                    value={
                      userDetails?.location === 'NA' ||
                        userDetails?.location === '0' ||
                        !userDetails?.location
                        ? null // This triggers the placeholder styling[NA]
                        : userDetails?.location
                    }
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaBirthdayCake}
                    label="Date of Birth"
                    value={formatDate(userDetails?.dateOfBirth)}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaTransgender}
                    label="Gender"
                    value={userDetails?.gender}
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={FaHeart}
                    label="Marital Status"
                    value={
                      userDetails?.maritalStatus === 'NA' ||
                        userDetails?.maritalStatus === '0' ||
                        !userDetails?.maritalStatus
                        ? null // This triggers the placeholder styling[NA]
                        : userDetails?.maritalStatus
                    }
                  />
                </GridItem>
                <GridItem>
                  <InfoRow
                    icon={MdContactPhone}
                    label="Emergency Contact"
                    value={(() => {
                      const name =
                        userDetails?.emgContactName &&
                          userDetails.emgContactName !== 'NA' &&
                          userDetails.emgContactName !== '0'
                          ? userDetails.emgContactName
                          : '';

                      const relation =
                        userDetails?.emgContactRelation &&
                          userDetails.emgContactRelation !== 'NA' &&
                          userDetails.emgContactRelation !== '0'
                          ? userDetails.emgContactRelation
                          : '';

                      const phone =
                        userDetails?.emgContactPhoneNo &&
                          userDetails.emgContactPhoneNo !== 'NA' &&
                          userDetails.emgContactPhoneNo !== '0'
                          ? userDetails.emgContactPhoneNo
                          : '';

                      //  Formatting rules
                      if (name && relation && phone)
                        return `${name} (${relation}) • ${phone}`;
                      if (name && relation) return `${name} (${relation})`;
                      if (name && phone) return `${name} • ${phone}`;
                      if (relation && phone) return `${relation} • ${phone}`;
                      if (name) return name;
                      if (relation) return relation;
                      if (phone) return phone;

                      return null;
                    })()}
                  />
                </GridItem>
              </Grid>
            </TabPanel>
          </TabPanels>
        </Tabs>
        {/* ==================== FOOTER SECTION ==================== */}
        <Flex
          justify="flex-end"
          p={6}
          borderTop="1px solid"
          borderColor="gray.100"
        >
          <Button
            colorScheme="blue"
            variant="outline"
            leftIcon={<FaKey />}
            onClick={handleOpenChangePasswordModal}
          >
            Change Password
          </Button>
        </Flex>
      </Box>
      {/* ==================== MODAL Section ==================== */}
      <Modal
        isOpen={isProfilePhotoModalOpen}
        closeOnOverlayClick={false}
        onClose={handleCloseProfilePhotoModal}
      >
        <ModalOverlay />
        <ModalContent
          borderRadius="xl"
          boxShadow="xl"
          bg="rgba(255, 255, 255, 0.9)"
          backdropFilter="blur(10px)"
          p={6}
          maxW="400px"
        >
          <ModalHeader textAlign="center" fontSize="lg" fontWeight="bold">
            Profile Picture
          </ModalHeader>
          <ModalCloseButton color="gray.700" />
          <ModalBody>
            <VStack spacing={5} align="center">
              {/* Large preview of current profile picture */}
              <Avatar
                size="2xl"
                name={userDetails?.name}
                src={profilePhoto || undefined}
                bg="gray.300"
                color="gray.700"
                borderWidth={3}
                borderColor="gray.500"
                boxShadow="lg"
              />
              {/* User information display */}
              <Text fontWeight="bold" fontSize="lg" color="gray.800">
                {userDetails?.name || 'Your Name'}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {userDetails?.emailId || 'your.email@example.com'}
              </Text>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={4} width="100%" justifyContent="center">
              {/* Change profile picture button */}
              <Button
                colorScheme="green"
                variant="solid"
                _hover={{ bg: 'green.600' }}
                onClick={() => {
                  openFileInput();
                  handleCloseProfilePhotoModal();
                }}
                leftIcon={<FaCamera />}
                size="md"
              >
                Change
              </Button>
              {/* Remove profile picture button */}
              <Button
                colorScheme="red"
                variant="outline"
                _hover={{ bg: 'red.100' }}
                onClick={handleDeleteProfilePhoto}
                leftIcon={<FaTrash />}
                size="md"
                isLoading={isPhotoSubmitting}
              >
                Remove
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
      {/* ==================== CHANGE PASSWORD MODAL ==================== */}
      <Modal
        isOpen={isChangePasswordModalOpen}
        closeOnOverlayClick={false}
        onClose={handleCloseChangePasswordModal}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box position="relative">
                <FormLabel htmlFor="old-password">
                  Current Password{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  id="old-password"
                  type={showOldPassword ? 'text' : 'password'}
                  placeholder="Enter your current password"
                  value={oldPassword}
                  onChange={(e) => {
                    setOldPassword(e.target.value);
                    setPasswordErrors((prev) => ({ ...prev, oldPassword: '' }));
                  }}
                  isInvalid={!!passwordErrors.oldPassword}
                  borderColor={
                    passwordErrors.oldPassword ? 'red.500' : undefined
                  }
                  autoComplete="off"
                  pr="2.5rem"
                />
                <Icon
                  as={showOldPassword ? MdVisibilityOff : MdVisibility}
                  position="absolute"
                  right="10px"
                  top="45px"
                  cursor="pointer"
                  onClick={() => setShowOldPassword((prev) => !prev)}
                  zIndex={2}
                />
                {passwordErrors.oldPassword && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {passwordErrors.oldPassword}
                  </Text>
                )}
              </Box>

              <Box position="relative">
                <FormLabel htmlFor="new-password">
                  New Password{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter your new password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setPasswordErrors((prev) => ({ ...prev, newPassword: '' }));
                  }}
                  isInvalid={!!passwordErrors.newPassword}
                  borderColor={
                    passwordErrors.newPassword ? 'red.500' : undefined
                  }
                  autoComplete="off"
                  pr="2.5rem"
                />
                <Icon
                  as={showNewPassword ? MdVisibilityOff : MdVisibility}
                  position="absolute"
                  right="10px"
                  top="45px"
                  cursor="pointer"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  zIndex={2}
                />
                {passwordErrors.newPassword && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {passwordErrors.newPassword}
                  </Text>
                )}
                {/* Show helper text only while the user has started typing and the password is still invalid */}
                {newPassword &&
                  validatePassword(newPassword) &&
                  !passwordErrors.newPassword && (
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      Must be at least 8 characters with uppercase, lowercase,
                      number, and special character
                    </Text>
                  )}
              </Box>

              <Box position="relative">
                <FormLabel htmlFor="confirm-password">
                  Confirm New Password{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your new password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setPasswordErrors((prev) => ({
                      ...prev,
                      confirmPassword: '',
                    }));
                  }}
                  isInvalid={!!passwordErrors.confirmPassword}
                  borderColor={
                    passwordErrors.confirmPassword ? 'red.500' : undefined
                  }
                  autoComplete="off"
                  pr="2.5rem"
                />
                <Icon
                  as={showConfirmPassword ? MdVisibilityOff : MdVisibility}
                  position="absolute"
                  right="10px"
                  top="45px"
                  cursor="pointer"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  zIndex={2}
                />
                {passwordErrors.confirmPassword && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {passwordErrors.confirmPassword}
                  </Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleChangePassword}
              isLoading={changingPassword}
            >
              Change Password
            </Button>
            <Button variant="ghost" onClick={handleCloseChangePasswordModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
